import { Badge } from 'antd';
import {
	ApiOutlined,
	BranchesOutlined,
	LockOutlined,
	QuestionCircleOutlined,
	RobotOutlined,
	ScanOutlined,
	ShareAltOutlined,
	ShopOutlined,
	ThunderboltOutlined,
} from '@ant-design/icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
	capabilitiesForArea,
	capabilityState,
	type Capability,
	type CapabilityState,
} from '../capabilities.js';
import { useRegion } from '../hooks/useRegion.js';
import {
	ActionTileGrid,
	MobileRootPage,
	MOBILE_ROOT_PRIMARY,
	RegionTag,
	RootHeader,
	SectionBlock,
} from '../components/MobileRootScaffold.js';

const ICONS: Record<string, ComponentType<{ style?: React.CSSProperties }>> = {
	patrol: ShopOutlined,
	aipatrol: RobotOutlined,
	scan: ScanOutlined,
	sharedevice: ShareAltOutlined,
	pos: BranchesOutlined,
	phonelive: ApiOutlined,
};

const PRIORITY_RANK: Record<string, number> = {
	P0: 0,
	P1: 1,
	P2: 2,
	P3: 3,
};

function sortCapabilities(items: Capability[]): Capability[] {
	return [...items].sort((a, b) => {
		const pa = PRIORITY_RANK[a.priority ?? 'P3'] ?? 9;
		const pb = PRIORITY_RANK[b.priority ?? 'P3'] ?? 9;
		return pa - pb || a.key.localeCompare(b.key);
	});
}

export default function WorkbenchTab() {
	const { t } = useTranslation();
	const region = useRegion();

	const available = useMemo(
		() => sortCapabilities(capabilitiesForArea('operations', region, ['available']).filter((cap) => cap.moduleKey)),
		[region],
	);
	const gated = useMemo(
		() => sortCapabilities(capabilitiesForArea('operations', region, ['permission_required', 'unknown']).filter((cap) => cap.moduleKey)),
		[region],
	);

	return (
		<MobileRootPage>
			<RootHeader
				icon={<ThunderboltOutlined />}
				title={t('ops.title')}
				subtitle={t('ops.subtitle')}
				meta={<RegionTag region={region} />}
			/>

			<CapabilitySection title={t('ops.available')} items={available} state="available" />
			{gated.length > 0 && (
				<CapabilitySection title={t('ops.permission_required')} items={gated} />
			)}
		</MobileRootPage>
	);
}

function CapabilitySection({
	title,
	items,
	state,
}: {
	title: string;
	items: Capability[];
	state?: CapabilityState;
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const region = useRegion();
	if (items.length === 0) {
		return (
			<SectionBlock title={title}>
				<div style={{ color: '#8a94a6', fontSize: 13, padding: '8px 4px' }}>
					{t('ops.empty')}
				</div>
			</SectionBlock>
		);
	}

	return (
		<SectionBlock title={title}>
			<ActionTileGrid
				items={items.map((cap) => {
					const tileState = state ?? capabilityState(cap, region);
					const Icon = ICONS[cap.key] ?? ThunderboltOutlined;
					const locked = tileState === 'permission_required' || tileState === 'unknown';
					return {
						key: cap.key,
						title: t(cap.labelKey),
						subtitle: locked ? (
							<>
								{t(stateLabelKey(tileState))}
								<span style={{ display: 'block', color: MOBILE_ROOT_PRIMARY, marginTop: 5 }}>
									{t('ops.view_status')}
								</span>
							</>
						) : t(stateLabelKey(tileState)),
						icon: <Icon />,
						onClick: () => navigate(cap.route),
						badge: locked ? (
							<Badge
								count={tileState === 'unknown' ? <QuestionCircleOutlined /> : <LockOutlined />}
								style={{ background: tileState === 'unknown' ? '#8b5cf6' : '#64748b' }}
							/>
						) : undefined,
					};
				})}
			/>
		</SectionBlock>
	);
}

function stateLabelKey(state: CapabilityState): string {
	if (state === 'permission_required') return 'capability.permission_required_short';
	if (state === 'unknown') return 'capability.unknown_short';
	if (state === 'region_unavailable') return 'capability.region_unavailable_short';
	return 'capability.available_short';
}
